'use client';

// Bloc "Cartes à icônes" (type `icon_cards`) pour les pages CMS.
// Affiche une grille de cartes texte SANS image : chaque carte = une icône
// lucide + un titre + un texte. Le nombre de colonnes (1 à 4) est choisi dans
// l'éditeur super-admin ; sur mobile la grille passe automatiquement à
// 1 colonne via `repeat(auto-fit, minmax(...))`.
//
// L'éditeur correspondant est IconCardsEditor.jsx (super-admin). Les deux
// utilisent le MÊME jeu d'icônes lucide.
import * as Icons from 'lucide-react';
import { applyTextStyle } from '@/lib/textStyle';

// Catalogue d'icônes lucide proposé par l'éditeur (~230 icônes). Cette liste
// DOIT rester strictement synchronisée — mêmes clés, même ordre — avec
// ICON_CARDS_ICONS de IconCardsEditor.jsx. Toutes ces clés sont des exports
// réels de lucide-react v0.445.0 (la version utilisée par la vitrine).
// Le rendu fait `Icons[card.icon]` avec fallback `Icons.Check` : cette liste
// sert de référence/documentation et garantit la cohérence éditeur ↔ rendu.
export const ICON_CARDS_ICONS = [
  // Statuts & validation
  'Check', 'CircleCheck', 'CircleCheckBig', 'CheckSquare', 'X', 'CircleX',
  'CircleAlert', 'TriangleAlert', 'Info', 'CircleHelp', 'Ban',
  // Mise en avant & qualité
  'Star', 'Heart', 'ThumbsUp', 'Sparkles', 'Award', 'Trophy',
  'Medal', 'Crown', 'Flag', 'Bookmark', 'BadgeCheck',
  // Documents & fichiers
  'FileText', 'File', 'FileCheck', 'FileSearch', 'FilePlus', 'FileSpreadsheet',
  'FileSignature', 'FileWarning', 'Folder', 'FolderOpen', 'FolderPlus', 'FolderCheck',
  'Archive', 'Clipboard', 'ClipboardCheck', 'ClipboardList', 'Book', 'BookOpen',
  'Newspaper', 'Receipt', 'ScrollText', 'NotebookPen',
  // Finance & comptabilité
  'Calculator', 'CreditCard', 'Banknote', 'Wallet', 'Coins', 'Euro',
  'DollarSign', 'PiggyBank', 'TrendingUp', 'TrendingDown', 'BarChart', 'BarChart3',
  'LineChart', 'PieChart', 'Percent', 'Landmark', 'Scale',
  // Communication
  'Mail', 'MailCheck', 'Send', 'Inbox', 'MessageCircle', 'MessageSquare',
  'MessagesSquare', 'Phone', 'PhoneCall', 'Smartphone', 'Megaphone', 'Bell',
  'BellRing', 'BellOff', 'AtSign', 'Hash', 'Share2', 'Mic',
  // Bâtiment & BTP
  'Building', 'Building2', 'Home', 'House', 'Warehouse', 'Store',
  'Factory', 'School', 'Hospital', 'Construction', 'HardHat',
  // Outils & équipement
  'Hammer', 'Wrench', 'Drill', 'Ruler', 'PaintRoller', 'Brush',
  'Cog', 'Settings', 'Settings2', 'Sliders', 'SlidersHorizontal', 'Plug',
  'PlugZap',
  // Transport & logistique
  'Truck', 'Car', 'Bus', 'Plane', 'Ship', 'Train',
  'Forklift', 'Package', 'PackageOpen', 'PackageCheck', 'PackageSearch', 'Box',
  'Fuel',
  // Temps & planning
  'Clock', 'Timer', 'AlarmClock', 'Calendar', 'CalendarDays', 'CalendarCheck',
  'CalendarClock', 'CalendarPlus', 'CalendarRange', 'History', 'Hourglass',
  // Utilisateurs & équipe
  'Users', 'User', 'UserPlus', 'UserCheck', 'UserCog', 'UserCircle',
  'Contact', 'PersonStanding', 'Handshake',
  // Sécurité
  'Shield', 'ShieldCheck', 'ShieldAlert', 'Lock', 'Unlock', 'Key',
  'KeyRound', 'Eye', 'EyeOff', 'Fingerprint',
  // Médias
  'Camera', 'Image', 'Film', 'Music', 'Play', 'Pause',
  'Video', 'Palette',
  // Mise en page & UI
  'Layout', 'LayoutGrid', 'LayoutDashboard', 'Grid3x3', 'List', 'ListChecks',
  'ListTodo', 'Table', 'Kanban', 'Menu', 'MoreHorizontal', 'Filter',
  // Navigation & actions
  'ArrowRight', 'ChevronRight', 'ExternalLink', 'Link', 'Move', 'RefreshCw',
  'RotateCw', 'Undo2',
  // Énergie & dynamisme
  'Zap', 'Rocket', 'Target', 'Gauge', 'Activity', 'Lightbulb',
  'Flame', 'Power',
  // Nature & environnement
  'Wind', 'Droplet', 'Droplets', 'Sun', 'Moon', 'Cloud',
  'Thermometer', 'Leaf', 'TreePine', 'Recycle',
  // Localisation & carte
  'Globe', 'MapPin', 'Map', 'Navigation', 'Compass', 'Route',
  // Recherche & commerce
  'Search', 'QrCode', 'Barcode', 'ShoppingCart', 'ShoppingBag', 'Tag',
  'Gift', 'Ticket', 'Briefcase',
  // Tech & infrastructure
  'Layers', 'Puzzle', 'Database', 'Server', 'Cpu', 'Monitor',
  'Laptop', 'Printer', 'CloudUpload', 'CloudDownload', 'Download', 'Upload',
  'Save', 'Network', 'Workflow', 'Wifi',
  // Édition
  'Trash2', 'Pencil', 'Copy', 'Paperclip', 'Pin', 'Plus',
  // Formation & idées
  'Brain', 'GraduationCap', 'Gavel',
];

export default function IconCardsBlock({ config }) {
  const cards = Array.isArray(config?.cards) ? config.cards : [];
  if (cards.length === 0) return null;

  // Colonnes desktop : bornées 1..4. minWidth adaptée pour que mobile
  // retombe naturellement sur 1 colonne (auto-fit + minmax).
  const cols = Math.max(1, Math.min(4, parseInt(config?.columns, 10) || 3));
  const minW = cols === 1 ? 320 : cols === 2 ? 320 : cols === 3 ? 260 : 220;

  const showHeader = config?.title || config?.subtitle;

  return (
    <section style={{ padding: '44px 0', background: '#fff' }}>
      <div className="container-page">
        {showHeader && (
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 50px' }}>
            {config.title && (
              <h2 style={applyTextStyle(config.title_style, {
                margin: '0 0 14px',
                fontSize: 'clamp(28px, 4vw, 40px)',
                fontWeight: 800, lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: 'var(--color-text)',
              })}>{config.title}</h2>
            )}
            {config.subtitle && (
              <p style={applyTextStyle(config.subtitle_style, {
                margin: 0,
                fontSize: 17, lineHeight: 1.55,
                color: '#64748b',
              })}>{config.subtitle}</p>
            )}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fit, minmax(${minW}px, 1fr))`,
          gap: 24,
        }}>
          {cards.map((card, i) => <IconCard key={i} card={card} />)}
        </div>
      </div>
    </section>
  );
}

function IconCard({ card }) {
  // Fallback sur Check si la clé d'icône est inconnue (ou absente).
  const Icon = (card?.icon && Icons[card.icon]) || Icons.Check;

  // Si la carte a un lien non vide, elle devient entièrement cliquable :
  // on rend une balise <a> à la place de <article>, avec curseur pointer et
  // un léger effet hover. Sans lien -> carte normale, comportement inchangé.
  const link = typeof card?.link === 'string' ? card.link.trim() : '';
  const isLink = link.length > 0;
  const Tag = isLink ? 'a' : 'article';

  const baseStyle = {
    display: 'flex', flexDirection: 'column',
    padding: 28,
    background: '#fff',
    border: '1px solid var(--color-border, #e5e7eb)',
    borderRadius: 14,
    height: '100%',
    transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.15s',
    ...(isLink ? { cursor: 'pointer', textDecoration: 'none', color: 'inherit' } : {}),
  };

  const linkProps = isLink
    ? {
        href: link,
        onMouseEnter: (e) => {
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
          e.currentTarget.style.borderColor = 'var(--color-primary)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        },
        onMouseLeave: (e) => {
          e.currentTarget.style.boxShadow = '';
          e.currentTarget.style.borderColor = 'var(--color-border, #e5e7eb)';
          e.currentTarget.style.transform = '';
        },
      }
    : {};

  return (
    <Tag {...linkProps} style={baseStyle}>
      <div style={{
        width: 52, height: 52,
        borderRadius: 14,
        background: 'rgba(226, 114, 40, 0.1)',
        color: 'var(--color-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 18,
      }}>
        <Icon size={24} strokeWidth={2.2} />
      </div>
      {card?.title && (
        <h3 style={{
          margin: '0 0 8px',
          fontSize: 18, fontWeight: 700, lineHeight: 1.3,
          color: 'var(--color-text)',
        }}>{card.title}</h3>
      )}
      {card?.text && (
        <p style={{
          margin: 0,
          fontSize: 15, lineHeight: 1.6,
          color: '#64748b',
        }}>{card.text}</p>
      )}
    </Tag>
  );
}
